import React from 'react';
import { Container, Row, Col, Card } from 'react-bootstrap';
import { Shield, Lock, Eye, UserCheck, Mail, Phone, Database } from 'lucide-react';

const PrivacyPolicy: React.FC = () => {
  return (
    <Container className="py-5">
      {/* <Row className="justify-content-center">
        <Col lg={10}>
          <Card className="border-0 shadow-lg"> */}
            <Card.Header className="bg-primary text-white text-center py-4">
              <div className="d-flex align-items-center justify-content-center gap-3 mb-3">
                <Shield size={40} />
                <h2 className="mb-0 fw-bold">Política de Privacidade</h2>
              </div>
              <p className="mb-0 opacity-90">
                SystemLoc - Sistema de Gestão para Lojas de Aluguel de Roupas
              </p>
              <small className="opacity-75">Última atualização: {new Date().toLocaleDateString('pt-BR')}</small>
            </Card.Header>
            
            <Card.Body className="p-4">
              {/* Introdução */}
              <section className="mb-5">
                <h3 className="text-primary mb-3">
                  <UserCheck size={24} className="me-2" />
                  1. Introdução
                </h3>
                <p>
                  A <strong>SystemLoc</strong> valoriza a privacidade e proteção dos dados pessoais de nossos usuários. 
                  Esta Política de Privacidade descreve como coletamos, utilizamos, armazenamos e protegemos suas 
                  informações pessoais, em conformidade com a Lei Geral de Proteção de Dados (LGPD - Lei nº 13.709/2018).
                </p>
                <p>
                  Esta política se aplica a todos os usuários do sistema SystemLoc, incluindo proprietários de lojas, 
                  funcionários e clientes finais que utilizam nossos serviços de gestão de aluguel de roupas.
                </p>
              </section>

              {/* Dados Coletados */}
              <section className="mb-5">
                <h3 className="text-primary mb-3">
                  <Database size={24} className="me-2" />
                  2. Dados Pessoais Coletados
                </h3>
                
                <h5 className="text-secondary mb-3">2.1 Dados de Usuários do Sistema (Lojas)</h5>
                <ul>
                  <li><strong>Nome completo</strong> - Para identificação e comunicação</li>
                  <li><strong>E-mail</strong> - Para autenticação e comunicação</li>
                  <li><strong>Senha</strong> - Criptografada para acesso seguro ao sistema</li>
                  <li><strong>Dados de uso do sistema</strong> - Para melhorias e suporte técnico</li>
                </ul>

                <h5 className="text-secondary mb-3">2.2 Dados de Clientes das Lojas</h5>
                <ul>
                  <li><strong>Nome completo</strong> - Para identificação e reservas</li>
                  <li><strong>CPF</strong> - Para identificação única e segurança</li>
                  <li><strong>Telefone</strong> - Para comunicação sobre reservas</li>
                  <li><strong>Medidas corporais</strong> - Para adequação dos produtos</li>
                  <li><strong>Observações pessoais</strong> - Para atendimento personalizado</li>
                  <li><strong>Histórico de reservas</strong> - Para gestão e melhorias no atendimento</li>
                  <li><strong>Preferências de produtos</strong> - Para sugestões personalizadas</li>
                </ul>

                <h5 className="text-secondary mb-3">2.3 Dados Técnicos</h5>
                <ul>
                  <li><strong>Endereço IP</strong> - Para segurança e análise de uso</li>
                  <li><strong>Informações do dispositivo</strong> - Para otimização da experiência</li>
                  <li><strong>Logs de acesso</strong> - Para segurança e auditoria</li>
                </ul>
              </section>

              {/* Finalidades */}
              <section className="mb-5">
                <h3 className="text-primary mb-3">
                  <Eye size={24} className="me-2" />
                  3. Finalidades do Tratamento de Dados
                </h3>
                
                <div className="row">
                  <div className="col-md-6 mb-3">
                    <div className="p-3 border rounded bg-light">
                      <h6 className="text-primary fw-bold">Gestão de Negócios</h6>
                      <ul className="mb-0 small">
                        <li>Controle de reservas e aluguéis</li>
                        <li>Gestão de estoque e produtos</li>
                      </ul>
                    </div>
                  </div>
                  
                  <div className="col-md-6 mb-3">
                    <div className="p-3 border rounded bg-light">
                      <h6 className="text-primary fw-bold">Comunicação</h6>
                      <ul className="mb-0 small">
                        <li>Confirmações de reservas</li>
                        <li>Lembretes de devolução</li>
                        <li>Suporte técnico</li>
                        <li>Comunicações importantes</li>
                      </ul>
                    </div>
                  </div>
                  
                  <div className="col-md-6 mb-3">
                    <div className="p-3 border rounded bg-light">
                      <h6 className="text-primary fw-bold">Segurança</h6>
                      <ul className="mb-0 small">
                        <li>Autenticação de usuários</li>
                        <li>Prevenção de fraudes</li>
                        <li>Monitoramento de segurança</li>
                        <li>Auditoria de acessos</li>
                      </ul>
                    </div>
                  </div>
                  
                  <div className="col-md-6 mb-3">
                    <div className="p-3 border rounded bg-light">
                      <h6 className="text-primary fw-bold">Melhorias</h6>
                      <ul className="mb-0 small">
                        <li>Desenvolvimento de funcionalidades</li>
                        <li>Análise de uso do sistema</li>
                        <li>Otimização de performance</li>
                        <li>Pesquisas de satisfação</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </section>

              {/* Base Legal */}
              <section className="mb-5">
                <h3 className="text-primary mb-3">4. Base Legal para Tratamento</h3>
                <p>
                  O tratamento de dados pessoais é realizado com base nas seguintes hipóteses legais previstas na LGPD:
                </p>
                <ul>
                  <li><strong>Execução de contrato</strong> - Para prestação dos serviços contratados</li>
                  <li><strong>Legítimo interesse</strong> - Para melhorias no sistema e segurança</li>
                  <li><strong>Consentimento</strong> - Para comunicações de marketing (quando aplicável)</li>
                  <li><strong>Cumprimento de obrigação legal</strong> - Para atendimento a requisitos legais</li>
                </ul>
              </section>

              {/* Compartilhamento */}
              <section className="mb-5">
                <h3 className="text-primary mb-3">
                  <Lock size={24} className="me-2" />
                  5. Compartilhamento de Dados
                </h3>
                <p>
                  <strong>Não compartilhamos seus dados pessoais com terceiros</strong>, exceto nas seguintes situações:
                </p>
                <ul>
                  <li><strong>Prestadores de serviços</strong> - Empresas que nos auxiliam na operação do sistema (hospedagem, segurança)</li>
                  <li><strong>Obrigação legal</strong> - Quando exigido por autoridades competentes</li>
                  <li><strong>Proteção de direitos</strong> - Para proteger nossos direitos ou de terceiros</li>
                  <li><strong>Consentimento expresso</strong> - Com sua autorização prévia</li>
                </ul>
                <div className="alert alert-info">
                  <strong>Importante:</strong> Todos os prestadores de serviços são obrigados a manter a mesma proteção 
                  e confidencialidade dos dados, conforme contratos específicos de proteção de dados.
                </div>
              </section>

              {/* Direitos */}
              <section className="mb-5">
                <h3 className="text-primary mb-3">6. Seus Direitos</h3>
                <p>Você possui os seguintes direitos sobre seus dados pessoais:</p>
                
                <div className="row">
                  <div className="col-md-6 mb-3">
                    <div className="border rounded p-3 h-100">
                      <h6 className="text-primary fw-bold">Acesso e Confirmação</h6>
                      <p className="small mb-0">Solicitar informações sobre quais dados temos sobre você</p>
                    </div>
                  </div>
                  
                  <div className="col-md-6 mb-3">
                    <div className="border rounded p-3 h-100">
                      <h6 className="text-primary fw-bold">Correção</h6>
                      <p className="small mb-0">Corrigir dados incompletos, inexatos ou desatualizados</p>
                    </div>
                  </div>
                  
                  <div className="col-md-6 mb-3">
                    <div className="border rounded p-3 h-100">
                      <h6 className="text-primary fw-bold">Exclusão</h6>
                      <p className="small mb-0">Solicitar a exclusão de dados desnecessários</p>
                    </div>
                  </div>
                  
                  <div className="col-md-6 mb-3">
                    <div className="border rounded p-3 h-100">
                      <h6 className="text-primary fw-bold">Portabilidade</h6>
                      <p className="small mb-0">Solicitar a portabilidade de seus dados</p>
                    </div>
                  </div>
                  
                  <div className="col-md-6 mb-3">
                    <div className="border rounded p-3 h-100">
                      <h6 className="text-primary fw-bold">Revogação</h6>
                      <p className="small mb-0">Revogar consentimento a qualquer momento</p>
                    </div>
                  </div>
                  
                  <div className="col-md-6 mb-3">
                    <div className="border rounded p-3 h-100">
                      <h6 className="text-primary fw-bold">Informações</h6>
                      <p className="small mb-0">Obter informações sobre entidades com quem compartilhamos dados</p>
                    </div>
                  </div>
                </div>
              </section>

              {/* Segurança */}
              <section className="mb-5">
                <h3 className="text-primary mb-3">
                  <Lock size={24} className="me-2" />
                  7. Medidas de Segurança
                </h3>
                <p>
                  Implementamos medidas técnicas e organizacionais para proteger seus dados pessoais:
                </p>
                
                <div className="row">
                  <div className="col-md-4 mb-3">
                    <div className="text-center p-3 border rounded">
                      <Shield size={32} className="text-primary mb-2" />
                      <h6 className="fw-bold">Criptografia</h6>
                      <p className="small mb-0">Dados criptografados em trânsito e em repouso</p>
                    </div>
                  </div>
                  
                  <div className="col-md-4 mb-3">
                    <div className="text-center p-3 border rounded">
                      <Lock size={32} className="text-primary mb-2" />
                      <h6 className="fw-bold">Controle de Acesso</h6>
                      <p className="small mb-0">Autenticação forte e controle de permissões</p>
                    </div>
                  </div>
                  
                  <div className="col-md-4 mb-3">
                    <div className="text-center p-3 border rounded">
                      <Eye size={32} className="text-primary mb-2" />
                      <h6 className="fw-bold">Monitoramento</h6>
                      <p className="small mb-0">Monitoramento contínuo de segurança</p>
                    </div>
                  </div>
                </div>
                
                <ul className="mt-3">
                  <li><strong>Firewall e proteção contra ataques</strong></li>
                  <li><strong>Backups regulares e seguros</strong></li>
                  <li><strong>Atualizações de segurança</strong></li>
                  <li><strong>Treinamento da equipe</strong> em proteção de dados</li>
                  <li><strong>Auditorias de segurança</strong> regulares</li>
                </ul>
              </section>

              {/* Retenção */}
              <section className="mb-5">
                <h3 className="text-primary mb-3">8. Retenção de Dados</h3>
                <p>
                  Mantemos seus dados pessoais pelo tempo necessário para cumprir as finalidades descritas nesta política:
                </p>
                <ul>
                  <li><strong>Dados de clientes ativos</strong> - Enquanto houver relação comercial</li>
                  <li><strong>Dados de usuários do sistema</strong> - Durante a vigência do contrato</li>
                  <li><strong>Dados para fins legais</strong> - Conforme exigido pela legislação</li>
                  <li><strong>Logs de segurança</strong> - Por período determinado por questões de segurança</li>
                </ul>
                <p>
                  Após o período de retenção, os dados são excluídos de forma segura e irreversível.
                </p>
              </section>

              {/* Contato */}
              <section className="mb-5">
                <h3 className="text-primary mb-3">
                  <Mail size={24} className="me-2" />
                  9. Contato e Exercício de Direitos
                </h3>
                <p>
                  Para exercer seus direitos ou esclarecer dúvidas sobre esta política, entre em contato conosco:
                </p>
                
                <div className="row">
                  <div className="col-md-6">
                    <div className="p-3 border rounded bg-light">
                      <h6 className="text-primary fw-bold">E-mail</h6>
                      <p className="mb-0">
                        <Mail size={16} className="me-2" />
                        contato.systemloc@gmail.com
                      </p>
                    </div>
                  </div>
                  
                  <div className="col-md-6">
                    <div className="p-3 border rounded bg-light">
                      <h6 className="text-primary fw-bold">Telefone</h6>
                      <p className="mb-0">
                        <Phone size={16} className="me-2" />
                        (62) 99855-1246
                      </p>
                    </div>
                  </div>
                </div>
                
                <div className="alert alert-warning mt-3">
                  <strong>Prazo de Resposta:</strong> Responderemos às suas solicitações em até 15 dias úteis, 
                  conforme previsto na LGPD.
                </div>
              </section>

              {/* Alterações */}
              <section className="mb-4">
                <h3 className="text-primary mb-3">10. Alterações nesta Política</h3>
                <p>
                  Esta Política de Privacidade pode ser atualizada periodicamente. Quando houver alterações 
                  significativas, notificaremos os usuários através do sistema ou por e-mail.
                </p>
                <p>
                  Recomendamos que você revise esta política regularmente para se manter informado sobre 
                  como protegemos seus dados pessoais.
                </p>
              </section>

              {/* Rodapé */}
              <hr />
              <div className="text-center text-muted">
                <p className="mb-2">
                  <strong>SystemLoc</strong> - Sistema de Gestão para Lojas de Aluguel de Roupas
                </p>
                <p className="mb-0 small">
                  Esta política está em conformidade com a Lei Geral de Proteção de Dados (LGPD - Lei nº 13.709/2018)
                </p>
              </div>
            </Card.Body>
          {/* </Card>
        </Col>
      </Row> */}
    </Container>
  );
};

export default PrivacyPolicy;
